import type { ReactNode } from 'react';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { AllocationField } from '@voll-intelligence/intelligence-ui-kit';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import { useGenUiI18n, type GenUiKey } from '../../../../shared/ui/i18n.js';
import {
  hasSelectField,
  type CheckoutField,
  type CheckoutPrefill,
} from '@voll-intelligence/genui-contract';
import { missingTravelerFields } from '../../core/one_click.js';
import {
  budgetShortfall,
  useCostCenterShortfall,
  type OneClickStage,
  type UseOneClickCheckout,
} from '../../core/use_one_click_checkout.js';
import { identityRows, TravelerFieldItem } from '@voll-intelligence/intelligence-ui-kit/checkout';
import { useCheckoutEnv } from '../../core/checkout_env.js';
import { ownerAllocationHint, ownsAllocation } from './allocation_owner.js';
import {
  FULL_ROW,
  JustificationItem,
  MotiveItem,
  ObservationItem,
  PrefilledMark,
  type Catalog,
  prefilledMarks,
} from './one_click_booking_fields.js';

/** Duas colunas a partir da régua de componente do DS (`--breakpoint-sm` no
 *  container `checkout`), uma abaixo dela. O degrau de toque (44px) é de
 *  ponteiro, não de largura: com `pointer: coarse` o `!` vence o `h-9!` da casca
 *  dos campos (`FIELD_CONTROL_SIZE`) e os inputs de texto (`.ctc-input`) crescem
 *  junto. `.cpa-fields` (Empresa · Centro de custo do `AllocationField`) e o
 *  contêiner dela se desfazem na grade, para os quatro campos dividirem as mesmas colunas. Exceção
 *  de propósito: nome e sobrenome de embarque seguem lado a lado no estreito (a
 *  `.ctc-traveler-row` do `EmbarkField`) — par curto, lido como um nome só. */
const GRID =
  'grid grid-cols-2 gap-3 @max-[theme(--breakpoint-sm)]/checkout:grid-cols-1 ' +
  '[&_.cpa-fields-frame]:contents [&_.cpa-fields]:contents ' +
  'pointer-coarse:[&_[data-slot=select-trigger]]:h-11! pointer-coarse:[&_[data-slot=popover-trigger]]:h-11! ' +
  'pointer-coarse:[&_.ctc-input]:min-h-11';

const TITLE = 'm-0 text-sm font-medium text-foreground';
const HINT = 'm-0 flex items-center gap-1.5 text-detail font-medium text-sand-11';

type Slice = UseOneClickCheckout['form']['travelers'][number];

/** Os campos de identidade que o perfil do viajante não tem, na ordem do drawer
 *  (`identityRows`): nome de embarque e documento ocupam a linha inteira. */
function MissingFields({
  flow,
  index,
  slice,
  missing,
  idPrefix,
}: {
  readonly flow: UseOneClickCheckout;
  readonly index: number;
  readonly slice: Slice;
  readonly missing: readonly CheckoutField[];
  readonly idPrefix: string;
}) {
  const rows = identityRows(missing);
  const wide = new Set<CheckoutField>([...rows.lead, ...rows.tail]);
  return (
    <div className={GRID}>
      {[...rows.lead, ...rows.paired, ...rows.tail].map((field) => (
        <div key={field.kind} className={wide.has(field) ? FULL_ROW : ''}>
          <TravelerFieldItem
            field={field}
            traveler={slice.form?.traveler}
            travelerDraft={slice.travelerDraft}
            idPrefix={idPrefix}
            onUpdateTraveler={(patch) => {
              flow.form.updateTraveler(index, patch);
            }}
          />
        </div>
      ))}
    </div>
  );
}

/** «Complete seus dados» de quem viaja sozinho: os campos que o perfil não tem.
 *  Para si, o que a pessoa preencher fica salvo no perfil (o BFF grava só o de
 *  quem está logado); para um colega, vale só para esta reserva. */
function CompleteDataSection({
  flow,
  missing,
  requesterId,
}: {
  readonly flow: UseOneClickCheckout;
  readonly missing: readonly CheckoutField[];
  readonly requesterId: string | undefined;
}) {
  const { t } = useGenUiI18n();
  const slice = flow.form.travelers[0];
  if (!slice) return null;
  const name = slice.form?.traveler?.name ?? slice.traveler.name;
  // Sem `requesterId` (o agente não soube quem pede), quem viaja sozinho fica
  // com a copy de si, como antes do roster: não há como afirmar que é colega.
  const self = requesterId === undefined || isSelf(slice, requesterId);
  const title = self
    ? t('productCheckout.oneClick.completeTitle')
    : t('productCheckout.oneClick.completeTitleGuest', { name });
  const hint = self
    ? t('productCheckout.oneClick.completeHint')
    : t('productCheckout.oneClick.completeHintGuest', { name });
  return (
    <section className="flex flex-col gap-3" aria-label={title}>
      <SectionHead title={title} hint={hint} />
      <MissingFields flow={flow} index={0} slice={slice} missing={missing} idPrefix="oc-" />
    </section>
  );
}

function isSelf(slice: Slice, requesterId: string | undefined): boolean {
  return Boolean(requesterId) && slice.traveler.id === requesterId;
}

/** O cabeçalho das duas seções: título à esquerda, a nota à direita (embaixo
 *  no estreito). */
function SectionHead({ title, hint }: { readonly title: string; readonly hint?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <h3 className={TITLE}>{title}</h3>
      {hint && (
        <p className={`${HINT} ml-auto @max-[theme(--breakpoint-sm)]/checkout:ml-0`}>{hint}</p>
      )}
    </div>
  );
}

/** O rateio de um viajante, com o saldo do centro de custo dele: só o
 *  insuficiente aparece, colado ao select; nunca trava o envio (o servidor
 *  decide, design spec §Budget). Montado só no estado pronto. */
function TravelerAllocation({
  flow,
  index,
  slice,
  catalog,
}: {
  readonly flow: UseOneClickCheckout;
  readonly index: number;
  readonly slice: Slice;
  readonly catalog: Catalog;
}) {
  const { t } = useGenUiI18n();
  const { copy: text, io } = useCheckoutEnv();
  const copy = text.fields;
  const searchCostCenters = io?.searchCostCenters;
  // Com um bloco só pagando o form já consulta o saldo dele (`form.budget`),
  // o mesmo da tela cheia; com vários, cada bloco consulta o seu.
  const shared = flow.form.budgetTravelerId !== undefined;
  const own = useCostCenterShortfall({
    enabled: !shared,
    productType: flow.productType,
    allocation: slice.allocations[0],
    total: flow.allocationShare,
    startDate: flow.startDate,
  });
  const insufficient = shared
    ? slice.traveler.id === flow.form.budgetTravelerId && budgetShortfall(flow.form.budget)
    : own;
  return (
    <AllocationField
      catalog={catalog}
      value={slice.allocations[0] ?? { companyId: '', costCenterId: '', projectId: '' }}
      searchCostCenters={searchCostCenters}
      copy={copy.allocation}
      projectRequired
      costCenterNotice={insufficient ? t('productCheckout.oneClick.budgetInsufficient') : undefined}
      editing
      onChange={(value) => {
        // O 1click não rateia: a escolha é sempre a linha única.
        flow.form.updateAllocations(index, [value]);
      }}
    />
  );
}

/** «Revise os dados abaixo»: rateio, motivo, a justificativa (só fora
 *  da política) e a observação. O ✦ marca o que veio do prefill e ainda não foi trocado;
 *  «Obrigatório» fica sob a escolha que falta. */
function ReviewSection({
  flow,
  prefill,
}: {
  readonly flow: UseOneClickCheckout;
  readonly prefill: CheckoutPrefill | undefined;
}) {
  const { t } = useGenUiI18n();
  const { form } = flow;
  const slice = form.travelers[0];
  const catalog = slice?.form;
  if (!slice || !catalog) return null;
  const marks = prefilledMarks(form, prefill);
  return (
    <section className="flex flex-col gap-3" aria-label={t('productCheckout.oneClick.reviewTitle')}>
      <SectionHead
        title={t('productCheckout.oneClick.reviewTitle')}
        hint={
          (marks.motive || Boolean(slice.allocations[0]?.companyId)) && (
            <>
              <PrefilledMark />
              {t('productCheckout.oneClick.prefilledHint')}
            </>
          )
        }
      />
      <div className={GRID}>
        <TravelerAllocation flow={flow} index={0} slice={slice} catalog={catalog} />
        <MotiveItem flow={flow} prefilled={marks.motive} />
        <JustificationItem flow={flow} catalog={catalog} prefilled={marks.justification} />
        <ObservationItem flow={flow} />
      </div>
    </section>
  );
}

/** O bloco de UM viajante no pedido com acompanhantes: o rateio dele e, só
 *  quando o perfil dele tem buraco, os dados que faltam. O que a pessoa
 *  preencher para si fica salvo no perfil (o BFF grava só o de quem está
 *  logado); para um colega vale só para esta reserva. */
function TravelerBlock({
  flow,
  index,
  slice,
  requesterId,
}: {
  readonly flow: UseOneClickCheckout;
  readonly index: number;
  readonly slice: Slice;
  readonly requesterId: string | undefined;
}) {
  const { t } = useGenUiI18n();
  const { traveler: labels } = useCheckoutEnv().copy;
  const catalog = slice.form;
  if (!catalog) return null;
  const name = catalog.traveler?.name ?? slice.traveler.name;
  const self = isSelf(slice, requesterId);
  const missing = missingTravelerFields(flow.fields, catalog);
  return (
    <section aria-label={name} className="flex flex-col gap-3 rounded-xl border border-border p-3">
      <div className="flex items-center gap-2">
        <h4 className="m-0 text-sm font-medium text-foreground">{name}</h4>
        {self && (
          <Badge variant="soft" color="neutral">
            {t('travelers.you')}
          </Badge>
        )}
      </div>
      {ownsAllocation(flow, slice) ? (
        <div className={GRID}>
          <TravelerAllocation flow={flow} index={index} slice={slice} catalog={catalog} />
        </div>
      ) : (
        <p className="m-0 text-detail text-sand-11">
          {ownerAllocationHint(flow, requesterId, labels, slice)}
        </p>
      )}
      {missing.length > 0 && (
        <>
          <p className="m-0 text-xs font-medium text-warning-strong">
            {self
              ? t('productCheckout.oneClick.completeHint')
              : t('productCheckout.oneClick.completeHintGuest', { name })}
          </p>
          <MissingFields
            flow={flow}
            index={index}
            slice={slice}
            missing={missing}
            idPrefix={`oc-t${String(index)}-`}
          />
        </>
      )}
    </section>
  );
}

/** «Revise os dados abaixo» com acompanhantes: um bloco por viajante (rateio
 *  e dados que faltam de cada um, como no voo do drawer) e, abaixo, o motivo,
 *  a justificativa (só fora da política) e a observação, que valem para o
 *  pedido inteiro. */
function MultiTravelerReview({
  flow,
  prefill,
  requesterId,
}: {
  readonly flow: UseOneClickCheckout;
  readonly prefill: CheckoutPrefill | undefined;
  readonly requesterId: string | undefined;
}) {
  const { t } = useGenUiI18n();
  const { form } = flow;
  const catalog = form.travelers[0]?.form;
  if (!catalog) return null;
  const marks = prefilledMarks(form, prefill);
  const prefilled =
    marks.motive || form.travelers.some((slice) => Boolean(slice.allocations[0]?.companyId));
  return (
    <section className="flex flex-col gap-3" aria-label={t('productCheckout.oneClick.reviewTitle')}>
      <SectionHead
        title={t('productCheckout.oneClick.reviewTitle')}
        hint={
          prefilled && (
            <>
              <PrefilledMark />
              {t('productCheckout.oneClick.prefilledHintMulti')}
            </>
          )
        }
      />
      {form.travelers.map((slice, index) => (
        <TravelerBlock
          key={slice.traveler.id}
          flow={flow}
          index={index}
          slice={slice}
          requesterId={requesterId}
        />
      ))}
      <h4 className="m-0 text-detail font-medium text-sand-11">
        {t('productCheckout.oneClick.bookingTitle')}
      </h4>
      <div className={GRID}>
        <MotiveItem flow={flow} prefilled={marks.motive} />
        <JustificationItem flow={flow} catalog={catalog} prefilled={marks.justification} />
        <ObservationItem flow={flow} />
      </div>
    </section>
  );
}

/** O envio anterior não deu certo: a falha deduzida (o turno acabou sem
 *  desfecho) aponta para a resposta; a do host (rede, teto) pede de novo. */
const NOTICE_KEY = {
  failed: 'productCheckout.oneClick.failed',
  unsent: 'productCheckout.oneClick.unsent',
} as const satisfies Record<
  NonNullable<Extract<OneClickStage, { kind: 'ready' }>['notice']>,
  GenUiKey
>;

function Notice({ children }: { readonly children: ReactNode }) {
  return (
    <p
      role="alert"
      className="m-0 flex items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-detail text-destructive"
    >
      <WarningCircleIcon size={16} weight="fill" aria-hidden className="shrink-0" />
      <span>{children}</span>
    </p>
  );
}

function FieldSkeletons({ fields }: { readonly fields: readonly string[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 @max-[theme(--breakpoint-sm)]/checkout:grid-cols-1">
      {fields.map((key) => (
        <div key={key} className="flex flex-col gap-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-9 w-full pointer-coarse:h-11" />
        </div>
      ))}
    </div>
  );
}

/** Esqueleto do estado «Carregando…»: o form traz Empresa, Centro de custo,
 *  Motivo e Justificativa juntos, então a conferência inteira espera por ele
 *  (os botões esperam no rodapé, `OneClickActions`). Com acompanhantes, um
 *  bloco por viajante e o motivo do pedido abaixo. */
function PanelSkeleton({
  withJustification,
  travelers,
}: {
  readonly withJustification: boolean;
  readonly travelers: number;
}) {
  const { t } = useGenUiI18n();
  const booking = ['motive', ...(withJustification ? ['justification'] : [])];
  return (
    <div aria-busy="true" className="flex flex-col gap-3">
      <span className="sr-only">{t('productCheckout.oneClick.loading')}</span>
      <Skeleton className="h-5 w-48" />
      {travelers > 1 ? (
        <>
          {Array.from({ length: travelers }, (_, index) => (
            <div key={index} className="flex flex-col gap-3 rounded-xl border border-border p-3">
              <Skeleton className="h-5 w-32" />
              <FieldSkeletons fields={['company', 'cost-center']} />
            </div>
          ))}
          <Skeleton className="h-4 w-40" />
          <FieldSkeletons fields={booking} />
        </>
      ) : (
        <FieldSkeletons fields={['company', 'cost-center', ...booking]} />
      )}
    </div>
  );
}

/** Os estágios em que o corpo recolhe: solicitando e todo registro. */
const COLLAPSED: ReadonlySet<OneClickStage['kind']> = new Set([
  'off',
  'submitting',
  'done',
  'indeterminate',
  'pending_approval',
  'failed',
]);

/** O corpo do card 1click entre o bloco do serviço e o fecho. Recolhe por
 *  inteiro enquanto solicita e depois de solicitado: o card vira registro. */
export function OneClickPanel({
  flow,
  prefill,
  requesterId,
}: {
  readonly flow: UseOneClickCheckout;
  readonly prefill: CheckoutPrefill | undefined;
  /** Quem está logado: o bloco dele diz que o que faltar fica salvo no perfil. */
  readonly requesterId: string | undefined;
}) {
  const { t } = useGenUiI18n();
  const { stage } = flow;
  if (COLLAPSED.has(stage.kind)) return null;
  const multiple = flow.travelerCount > 1;
  const missing =
    stage.kind === 'ready' && !multiple
      ? missingTravelerFields(flow.fields, flow.form.travelers[0]?.form)
      : [];
  return (
    <>
      <Separator />
      {stage.kind === 'loading' && (
        <PanelSkeleton
          withJustification={!flow.withinPolicy && hasSelectField(flow.fields, 'justification')}
          travelers={flow.travelerCount}
        />
      )}
      {stage.kind === 'error' && (
        <div className="flex flex-col items-start gap-2">
          <Notice>{t('productCheckout.oneClick.loadError')}</Notice>
          {/* Falha passageira do upstream (#5670): tentar de novo ali mesmo,
              sem perder a conversa; a tela cheia segue no rodapé. */}
          <Button size="sm" variant="outline" onClick={flow.form.reload}>
            {t('common.tryAgain')}
          </Button>
        </div>
      )}
      {stage.kind === 'ready' && (
        <>
          {stage.notice && <Notice>{t(NOTICE_KEY[stage.notice])}</Notice>}
          {multiple ? (
            <MultiTravelerReview flow={flow} prefill={prefill} requesterId={requesterId} />
          ) : (
            <>
              {missing.length > 0 && (
                <>
                  <CompleteDataSection flow={flow} missing={missing} requesterId={requesterId} />
                  <Separator />
                </>
              )}
              <ReviewSection flow={flow} prefill={prefill} />
            </>
          )}
        </>
      )}
    </>
  );
}
